import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import API from "@src/API.js";
import { toast } from "react-toastify";
import "../../style/scss/Recruit.scss";

const STATUS = {
  Active: 1,
  Expired: 2,
  Declined: 3,
  Shortlisted: 4,
};

function statusLabel(status) {
  switch (Number(status)) {
    case STATUS.Shortlisted:
      return "Shortlisted";
    case STATUS.Declined:
      return "Declined";
    case STATUS.Expired:
      return "Expired";
    default:
      return "Active";
  }
}

/**
 * Job match review UI. Use on /jobMatches/:id or embed with jobId + embedded.
 */
export default function JobMatchReview({
  jobId: jobIdProp,
  embedded = false,
  jobTitle: jobTitleProp,
  employerId: employerIdProp,
}) {
  const { id: idStr } = useParams();
  const jobId = Number.parseInt(jobIdProp ?? idStr, 10) || 0;
  const location = useLocation();
  const navigate = useNavigate();
  const [breadcrumb] = useState([
    ...(location.state?.breadcrumb || [{ label: "Hiring", route: "/recruit" }]),
    { label: "Match results", route: `/jobMatches/${jobId}` },
  ]);
  const [jobTitle, setJobTitle] = useState(
    jobTitleProp || location.state?.jobTitle || `Job #${jobId}`
  );
  const [employerId, setEmployerId] = useState(
    employerIdProp || location.state?.employerID || location.state?.EmployerID || ""
  );
  const [candidates, setCandidates] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jobSkills, setJobSkills] = useState([]);
  const [screeningDraft, setScreeningDraft] = useState({});

  async function load() {
    if (!jobId) return;
    setLoading(true);
    try {
      const [candResp, diagResp, jobResp] = await Promise.all([
        API.getAsync(`/JobCandidates?jobId=${jobId}`),
        API.getAsync(`/MatchDiagnostics?jobId=${jobId}`),
        API.getAsync(`/JobPosting?id=${jobId}`),
      ]);
      if (candResp?.ok) setCandidates(candResp.jsonData || []);
      else toast.error("Could not load candidates");

      if (diagResp?.ok) setDiagnostics(diagResp.jsonData);
      if (jobResp?.ok && jobResp.jsonData) {
        const job = jobResp.jsonData;
        setJobTitle(job.Title || job.title || jobTitle);
        setEmployerId(job.EmployerID || job.employerID || employerId);
        const skills = job.Skills || job.skills || [];
        setJobSkills(
          skills
            .map((s) => ({
              id: s.SkillID ?? s.skillID ?? s.ID ?? s.id,
              name: s.SkillName || s.skillName || s.Name || s.name || `Skill ${s.SkillID ?? s.skillID}`,
              level: s.Level ?? s.level ?? 3,
            }))
            .filter((s) => s.id)
        );
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [jobId]);

  async function setStatus(candidateId, status) {
    setBusy(true);
    try {
      const resp = await API.postAsync("/JobCandidate/Status", {
        JobCandidateID: candidateId,
        Status: status,
      });
      if (resp?.ok) {
        toast.success(`Marked ${statusLabel(status)}`);
        setCandidates((prev) =>
          prev.map((c) =>
            (c.ID || c.id) === candidateId
              ? { ...c, Status: status, StatusName: statusLabel(status) }
              : c
          )
        );
      } else {
        toast.error(resp?.jsonData?.message || "Could not update status");
      }
    } finally {
      setBusy(false);
    }
  }

  async function rerunMatch() {
    setBusy(true);
    try {
      const resp = await API.postAsync(`/FindCandidates?jobId=${jobId}`, {});
      if (resp?.ok) {
        toast.success("Matching complete");
        await load();
      } else {
        toast.error("Match run failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function notifyShortlisted() {
    setBusy(true);
    try {
      const resp = await API.postAsync("/JobCandidates/Notify", { JobID: jobId });
      if (resp?.ok) {
        const n = resp.jsonData?.notified ?? 0;
        toast.success(`Notified ${n} shortlisted candidate(s)`);
        await load();
      } else {
        toast.error(resp?.jsonData?.message || "Notify failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function proposeInterview(candidateId) {
    const when = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    when.setMinutes(0, 0, 0);
    setBusy(true);
    try {
      const resp = await API.postAsync("/JobCandidate/Interview", {
        JobCandidateID: candidateId,
        Action: "Propose",
        ProposedUtc: when.toISOString(),
        Notes: "Intro screen",
      });
      if (resp?.ok) {
        toast.success("Interview proposed");
        await load();
      } else {
        toast.error(resp?.jsonData?.message || "Could not propose interview");
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitSurvey(candidateId) {
    setBusy(true);
    try {
      const resp = await API.postAsync("/JobCandidate/Survey", {
        JobCandidateID: candidateId,
        Role: "Recruiter",
        Rating: 4,
        Notes: "Solid interview",
      });
      if (resp?.ok) {
        toast.success("Questionnaire saved");
        await load();
      } else {
        toast.error(resp?.jsonData?.message || "Survey failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function makeOffer(candidateId) {
    setBusy(true);
    try {
      const resp = await API.postAsync("/JobCandidate/Offer", {
        JobCandidateID: candidateId,
        Action: "Make",
        Amount: 120000,
        Notes: "Base salary",
      });
      if (resp?.ok) {
        toast.success("Offer made");
        await load();
      } else {
        toast.error(resp?.jsonData?.message || "Offer failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function draftFor(candidateId) {
    const existing = screeningDraft[candidateId];
    if (existing) return existing;
    const first = jobSkills[0];
    return {
      skillId: first?.id || "",
      mode: "1",
      skillLevelId: String(first?.level || 3),
    };
  }

  function updateDraft(candidateId, patch) {
    setScreeningDraft((prev) => ({
      ...prev,
      [candidateId]: { ...draftFor(candidateId), ...prev[candidateId], ...patch },
    }));
  }

  async function requestScreening(candidateId) {
    const draft = draftFor(candidateId);
    if (!draft.skillId) {
      toast.error("Job has no skills to screen against");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        JobCandidateID: candidateId,
        SkillID: Number(draft.skillId),
        Mode: Number(draft.mode),
      };
      if (Number(draft.mode) === 1) payload.SkillLevelID = Number(draft.skillLevelId);
      const resp = await API.postAsync("/Skill/Test/ScreeningInvite", payload);
      if (resp?.ok) {
        toast.success("Screening invite sent");
        await load();
      } else {
        toast.error(resp?.jsonData?.message || "Screening invite failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function screeningLabel(c) {
    const status = c.ScreeningStatus || c.screeningStatus;
    if (!status) return "—";
    const score = c.ScreeningScore ?? c.screeningScore;
    const level = c.ScreeningSkillLevel ?? c.screeningSkillLevel;
    const mode = c.ScreeningMode ?? c.screeningMode;
    const modeLabel = Number(mode) === 2 ? "Type2" : "Type1";
    let text = `${status} (${modeLabel})`;
    if (status === "Completed" && score != null) {
      text += ` · ${score}%`;
      if (level) text += ` · L${level}`;
    }
    return text;
  }

  const shortlistedCount = candidates.filter(
    (c) => Number(c.Status ?? c.status) === STATUS.Shortlisted
  ).length;
  const scarce =
    diagnostics &&
    (diagnostics.IsScarce === true ||
      diagnostics.isScarce === true ||
      Number(diagnostics.CandidatesPersisted ?? diagnostics.candidatesPersisted ?? 0) < 3);
  const advice = diagnostics?.Advice || diagnostics?.advice || [];
  const payNeg = diagnostics?.PayNegotiation || diagnostics?.payNegotiation;
  const showPayNeg = payNeg && (payNeg.Applicable === true || payNeg.applicable === true);

  if (!jobId) {
    return (
      <div className={`JobMatchReview${embedded ? " JobMatchReview--embedded" : " pagebody"}`}>
        <p className="fieldHint">Save the job posting first to review matches.</p>
      </div>
    );
  }

  const body = (
    <>
      <div className="matchReviewHeader">
        {!embedded ? <h1>Match results</h1> : null}
        {!embedded ? <p className="byline">{jobTitle}</p> : null}
        <div className="matchReviewActions">
          <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={rerunMatch}>
            Re-run matching
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            disabled={busy || shortlistedCount === 0}
            onClick={notifyShortlisted}
          >
            Notify shortlisted ({shortlistedCount})
          </CustomButton>
          {!embedded ? (
            <>
              <Link
                to={`/employerRecruitEdit/${employerId || ""}`}
                state={{
                  ...(location.state || {}),
                  employerID: employerId,
                  jobPostingModalId: jobId,
                }}
              >
                Edit job requirements
              </Link>
              <CustomButton type="button" variant="outlined" color="secondary" onClick={() => navigate(-1)}>
                Back
              </CustomButton>
            </>
          ) : null}
        </div>
      </div>

      {scarce ? (
        <section className="matchAdvice">
          <h2>Few matches — flexibility tips</h2>
          <p>
            The system found{" "}
            {diagnostics?.CandidatesPersisted ?? diagnostics?.candidatesPersisted ?? 0} ranked
            candidate(s). Relax the highest-impact constraints below to widen results.
          </p>
          {advice.length === 0 ? (
            <p>No constraint diagnostics yet. Re-run matching after saving the job.</p>
          ) : (
            <ul>
              {advice.slice(0, 6).map((a) => (
                <li key={a.ConstraintKey || a.constraintKey}>
                  <strong>
                    {a.ConstraintKey || a.constraintKey} ({a.Count ?? a.count})
                  </strong>
                  <div>{a.Guidance || a.guidance}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {showPayNeg ? (
        <section className="matchPayNegotiation" data-testid="pay-negotiation">
          <h2>Pay band negotiation</h2>
          <p>
            Posted max: $
            {Number(payNeg.PostedMaxCompensation ?? payNeg.postedMaxCompensation ?? 0).toLocaleString()}
            {" · "}
            Skill-qualified seekers: {payNeg.SkillQualifiedCount ?? payNeg.skillQualifiedCount ?? 0}
            {" · "}
            Above posted max: {payNeg.BlockedByPayCount ?? payNeg.blockedByPayCount ?? 0}
          </p>
          <p>
            Suggested band: $
            {Number(payNeg.SuggestedP25 ?? payNeg.suggestedP25 ?? 0).toLocaleString()} – $
            {Number(payNeg.SuggestedP75 ?? payNeg.suggestedP75 ?? 0).toLocaleString()}
            {" (median $"}
            {Number(payNeg.SuggestedP50 ?? payNeg.suggestedP50 ?? 0).toLocaleString()})
          </p>
          <p className="byline">{payNeg.Guidance || payNeg.guidance}</p>
        </section>
      ) : null}

      <section className="matchCandidates">
        <h2>System-ranked candidates</h2>
        <p className="byline">
          Ranked by the matching algorithm — not a search. Shortlist, notify, then review interest
          responses.
        </p>
        {loading ? <p>Loading…</p> : null}
        {!loading && candidates.filter((c) => Number(c.Status ?? c.status) !== STATUS.Expired).length === 0 ? (
          <p>No active matches yet. Save the job with skills or re-run matching.</p>
        ) : (
          <table className="matchCandidatesTable">
            <thead>
              <tr>
                <th>Score</th>
                <th>Candidate</th>
                <th>Interest</th>
                <th>Skills</th>
                <th>Status</th>
                <th>Notified</th>
                <th>Opening response</th>
                <th>Interview</th>
                <th>Screening</th>
                <th>Offer / onboard</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates
                .filter((c) => Number(c.Status ?? c.status) !== STATUS.Expired)
                .map((c) => {
                  const cid = c.ID || c.id;
                  const status = Number(c.Status ?? c.status);
                  const skills = c.KeySkills || c.keySkills || [];
                  const interviewStatus = c.InterviewStatus || c.interviewStatus;
                  const interviewWhen = c.InterviewProposedUtc || c.interviewProposedUtc;
                  const offerStatus = c.OfferStatus || c.offerStatus;
                  const offerAmount = c.OfferAmount ?? c.offerAmount;
                  const onboard = c.OnboardingStarted || c.onboardingStarted;
                  const survey = c.QuestionnaireSummary || c.questionnaireSummary;
                  return (
                    <tr key={cid} className={status === STATUS.Declined ? "declined" : ""}>
                      <td>{c.Score ?? c.score}</td>
                      <td>{c.PersonName || c.personName}</td>
                      <td>{c.InterestLevelName || c.interestLevelName || "—"}</td>
                      <td>{skills.length ? skills.join(", ") : "—"}</td>
                      <td>{c.StatusName || statusLabel(status)}</td>
                      <td>{c.Notified || c.notified ? "Yes" : "—"}</td>
                      <td>{c.OpeningInterest || c.openingInterest || "—"}</td>
                      <td>
                        {interviewStatus
                          ? `${interviewStatus}${interviewWhen ? ` @ ${new Date(interviewWhen).toLocaleString()}` : ""}`
                          : "—"}
                        {survey ? <div className="muted">{survey}</div> : null}
                      </td>
                      <td>
                        <div>{screeningLabel(c)}</div>
                        {jobSkills.length > 0 ? (
                          <div className="screeningInviteControls">
                            <select
                              aria-label={`Screening skill for ${c.PersonName || c.personName}`}
                              value={String(draftFor(cid).skillId)}
                              onChange={(e) => updateDraft(cid, { skillId: e.target.value })}
                            >
                              {jobSkills.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                            <select
                              aria-label={`Screening mode for ${c.PersonName || c.personName}`}
                              value={String(draftFor(cid).mode)}
                              onChange={(e) => updateDraft(cid, { mode: e.target.value })}
                            >
                              <option value="1">Type 1 certify</option>
                              <option value="2">Type 2 discover</option>
                            </select>
                            {String(draftFor(cid).mode) === "1" ? (
                              <select
                                aria-label={`Screening level for ${c.PersonName || c.personName}`}
                                value={String(draftFor(cid).skillLevelId)}
                                onChange={(e) => updateDraft(cid, { skillLevelId: e.target.value })}
                              >
                                <option value="1">Beginner</option>
                                <option value="2">Junior</option>
                                <option value="3">Intermediate</option>
                                <option value="4">Senior</option>
                                <option value="5">Guru</option>
                              </select>
                            ) : null}
                            <CustomButton
                              type="button"
                              variant="contained"
                              color="primary"
                              disabled={busy}
                              onClick={() => requestScreening(cid)}
                            >
                              Request screening
                            </CustomButton>
                          </div>
                        ) : (
                          <div className="muted">Add job skills to invite</div>
                        )}
                      </td>
                      <td>
                        {offerStatus
                          ? `${offerStatus}${offerAmount != null ? ` $${offerAmount}` : ""}`
                          : "—"}
                        {onboard ? <div>Onboarding started</div> : null}
                      </td>
                      <td className="actions">
                        <CustomButton
                          type="button"
                          variant="contained"
                          color="primary"
                          disabled={busy || status === STATUS.Shortlisted}
                          onClick={() => setStatus(cid, STATUS.Shortlisted)}
                        >
                          Shortlist
                        </CustomButton>
                        <CustomButton
                          type="button"
                          variant="contained"
                          color="error"
                          disabled={busy || status === STATUS.Declined}
                          onClick={() => setStatus(cid, STATUS.Declined)}
                        >
                          Decline
                        </CustomButton>
                        {status === STATUS.Shortlisted ? (
                          <>
                            <CustomButton
                              type="button"
                              variant="contained"
                              color="primary"
                              disabled={busy}
                              onClick={() => proposeInterview(cid)}
                            >
                              Propose interview
                            </CustomButton>
                            <CustomButton
                              type="button"
                              variant="contained"
                              color="primary"
                              disabled={busy}
                              onClick={() => submitSurvey(cid)}
                            >
                              Questionnaire
                            </CustomButton>
                            <CustomButton
                              type="button"
                              variant="contained"
                              color="primary"
                              disabled={busy}
                              onClick={() => makeOffer(cid)}
                            >
                              Make offer
                            </CustomButton>
                          </>
                        ) : null}
                        {status === STATUS.Declined || status === STATUS.Shortlisted ? (
                          <CustomButton
                            type="button"
                            variant="outlined"
                            color="secondary"
                            disabled={busy}
                            onClick={() => setStatus(cid, STATUS.Active)}
                          >
                            Reset
                          </CustomButton>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );

  if (embedded) {
    return (
      <div className="JobMatchReview JobMatchReview--embedded" data-testid="job-posting-matches">
        {body}
      </div>
    );
  }

  return (
    <div className="pagebody JobMatchReview">
      <BreadCrumb props={breadcrumb} />
      <div className="tabbody">{body}</div>
    </div>
  );
}
